import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

/**
 * Get week key for a given date (Sunday-Saturday weeks)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Week key in format "YYYY-W##" (e.g., "2025-W04")
 */
export const getWeekKey = (dateString) => {
    if (!dateString || dateString === 'No Date') return null;

    const date = dayjs.tz(dateString, 'America/New_York');

    // Get the Sunday of the week this date belongs to
    const sunday = date.day(0); // Go to Sunday of current week

    // Use the year from the Sunday
    const year = sunday.year();

    // Calculate week number based on Sunday start
    const week = sunday.week();

    return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Get week bounds (Sunday-Saturday) for a given week key
 * @param {string} weekKey - Week key in format "YYYY-W##"
 * @returns {object} Object with start and end dates { start: "YYYY-MM-DD", end: "YYYY-MM-DD" }
 */
export const getWeekBounds = (weekKey) => {
    if (!weekKey) return null;

    const [year, weekNum] = weekKey.split('-W').map(Number);

    // Start with the first day of the year and get to the correct week (Sunday-based)
    const start = dayjs()
        .tz('America/New_York')
        .year(year)
        .week(weekNum)
        .day(0); // Sunday

    // End is Saturday (6 days after Sunday)
    const end = start.add(6, 'day');

    return {
        start: start.format('MMM D'),
        end: end.format('MMM D')
    };
};

/**
 * Group an array of dates by their week
 * @param {Array<string>} dates - Array of date strings in YYYY-MM-DD format
 * @returns {object} Object mapping week keys to arrays of dates
 */
export const groupDatesByWeek = (dates) => {
    if (!dates || !Array.isArray(dates)) return {};

    const grouped = {};

    dates.forEach(date => {
        if (date === 'No Date') return; // Skip invalid dates

        const weekKey = getWeekKey(date);
        if (!weekKey) return;

        if (!grouped[weekKey]) {
            grouped[weekKey] = [];
        }
        grouped[weekKey].push(date);
    });

    // Sort dates within each week
    Object.keys(grouped).forEach(weekKey => {
        grouped[weekKey].sort((a, b) => {
            return dayjs.tz(a, 'America/New_York').valueOf() -
                dayjs.tz(b, 'America/New_York').valueOf();
        });
    });
    return grouped;
};

/**
 * Format week key into human-readable date range
 * @param {string} weekKey - Week key in format "YYYY-W##"
 * @returns {string} Formatted week range (e.g., "Jan 19 - Jan 25")
 */
export const formatWeekHeader = (weekKey) => {
    if (!weekKey) return '';

    const bounds = getWeekBounds(weekKey);
    if (!bounds) return weekKey;

    return `Week of ${bounds.start} - ${bounds.end}`;
};

/**
 * Check if a date falls on a valid delivery day (Sunday - Thursday)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} True if date is Sun-Thu, false otherwise
 */
export const isValidDeliveryDayInWeek = (dateString) => {
    if (!dateString || dateString === 'No Date') return false;

    const date = dayjs.tz(dateString, 'America/New_York');
    const dayOfWeek = date.day(); // 0 = Sunday, 6 = Saturday

    // Valid delivery days: Sunday (0) through Thursday (4)
    return dayOfWeek >= 0 && dayOfWeek <= 4;
};
